// apps/web/src/modules/synthesis/OverviewPanel.tsx
import { useCallback, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query/keys";
import { useSession } from "@/features/auth/hooks/useSession";
import { getActiveWorkspaceId } from "@/lib/api/workspace-context";
import {
  getProfileSummary,
  refreshProfileSummary,
} from "@/lib/api/endpoints/profile-interview";
import "./OverviewPanel.css";

export function OverviewPanel() {
  const { token } = useSession();
  const ws = getActiveWorkspaceId();
  const queryClient = useQueryClient();
  const queryKey = queryKeys.synthesis.tab(ws, "overview");
  const [busy, setBusy] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey,
    queryFn: () => getProfileSummary(token!, ws),
    enabled: Boolean(token),
    staleTime: 5 * 60 * 1000,
  });

  const profile = isLoading ? null : (data?.profile ?? "");

  const regenerate = useCallback(async () => {
    if (!token) return;
    setBusy(true);
    try {
      const r = await refreshProfileSummary(token, ws);
      queryClient.setQueryData(queryKey, r);
    } finally {
      setBusy(false);
    }
  }, [token, ws, queryClient, queryKey]);

  const paragraphs = (profile ?? "")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean);

  return (
    <div className="ov-wrap">
      <div className="ov-purpose">
        This is where you teach Xavior about you. The more it knows — your people,
        work, history, travel, and what you're watching — the more it personalizes
        your chats and daily briefings.
      </div>

      <div className="ov-head">
        <span className="ov-head-title">Your profile</span>
      </div>

      {profile === null ? (
        <div className="ov-muted">Loading…</div>
      ) : paragraphs.length === 0 ? (
        <div className="ov-empty">
          <p className="ov-muted">
            Xavior hasn't written your profile yet. Tell it about yourself, then
            generate.
          </p>
          <button className="ov-generate" onClick={() => void regenerate()} disabled={busy}>
            {busy ? "Writing…" : "Generate profile"}
          </button>
        </div>
      ) : (
        <div className="ov-profile">
          {paragraphs.map((p, i) => (
            <p key={i}>{p}</p>
          ))}
        </div>
      )}

      {paragraphs.length > 0 && (
        <button
          className="ov-refresh"
          onClick={() => void regenerate()}
          disabled={busy}
        >
          {busy ? "Refreshing…" : "Refresh synthesis"}
        </button>
      )}
    </div>
  );
}
